import {
  Calculate as CalculateIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { getUser } from 'lib/api';
import { getCreditLineByProduct } from 'lib/api/creditLine';
import { SGR_TYPES } from 'lib/consts';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import {
  calculateRiskPremium,
  RiskPremiumRates,
} from 'lib/functions/riskPremiumCalculator';
import { formatMoney } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

interface RiskPremiumCalculation {
  amount: number;
  days: number;
  conversionRate: number;
  type: string;
  quotedCurrency: string;
  warrantyRates?: RiskPremiumRates;
}

const useCreditLineData = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const [subProductsData, setSubProductsData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCreditLineData = async () => {
    try {
      setIsLoading(true);
      const profile = await getUser(dispatch, { id: auth.user?.id });
      if (!profile) throw new Error('Profile not found');
      const businessIdentifier = profile.company.taxIdentifier;
      const creditLine = await getCreditLineByProduct(
        dispatch,
        businessIdentifier,
        'sgr',
      );
      const data = creditLine?.subProducts || creditLine || [];
      setSubProductsData(data);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  return {
    subProductsData,
    isLoading,
    error,
    fetchCreditLineData,
  };
};

const useGetSubProductConcentration = (
  type: string,
  amount: number,
  days: number,
  conversionRate: number,
  warrantyRates?: RiskPremiumRates,
) => {
  const { subProductsData, isLoading, error, fetchCreditLineData } =
    useCreditLineData();

  const calculationBreakdown = calculateRiskPremium({
    amount,
    days,
    conversionRate,
    type,
    rates: warrantyRates,
    subProductsData,
    useConcentrationRates: !warrantyRates,
  });

  const totalPremium = calculationBreakdown?.totalPremium ?? 0;

  return {
    isLoading,
    error,
    totalPremium,
    fetchCreditLineData,
  };
};

const RiskPremiumCalculator: React.FC<RiskPremiumCalculation> = ({
  amount,
  days,
  conversionRate,
  type,
  quotedCurrency,
  warrantyRates,
}) => {
  const { isLoading, totalPremium, fetchCreditLineData } =
    useGetSubProductConcentration(
      type,
      amount,
      days,
      conversionRate,
      warrantyRates,
    );

  useEffect(() => {
    void fetchCreditLineData();
  }, []);

  if (isLoading) return <CenteredCircularProgress />;

  return (
    <Paper sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
      {/* Header Section */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Box
          sx={{
            backgroundColor: 'primary.main',
            borderRadius: '50%',
            p: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          <CalculateIcon sx={{ fontSize: '1rem' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: '1rem',
              lineHeight: 1.1,
            }}
          >
            Cálculo de Prima de Riesgo
          </Typography>
          <Chip
            label="SGR"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: '18px', fontSize: '0.65rem' }}
          />
        </Box>
        {totalPremium > 0 && (
          <Box sx={{ textAlign: 'right' }}>
            <Stack
              direction="row"
              alignItems="start"
              justifyContent="flex-end"
              spacing={0.75}
            >
              <Typography variant="h4" color="primary">
                {formatMoney(totalPremium)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                CLP
              </Typography>
            </Stack>
          </Box>
        )}
      </Stack>
      <Box sx={{ textAlign: 'center' }}>
        {amount > 0 &&
        days > 0 &&
        conversionRate > 0 &&
        type &&
        type.trim() !== '' ? (
          <Box>
            <Box
              sx={{
                mb: 1,
                p: 1,
                backgroundColor: 'rgba(76, 175, 80, 0.08)',
                borderRadius: 0.5,
              }}
            >
              <Typography
                variant="body2"
                color="success.main"
                sx={{ fontWeight: 600, fontSize: '0.75rem' }}
              >
                ✓ Cálculo completado
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.25, fontSize: '0.65rem' }}
              >
                Monto: {formatMoney(amount)} {quotedCurrency} • Días: {days} •
                Tipo: {SGR_TYPES.find((t) => t.value === type)?.label}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ pb: 1.5 }}>
            <WarningIcon
              sx={{ fontSize: '2rem', color: 'text.disabled', mb: 0.75 }}
            />
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                fontWeight: 500,
                fontSize: '0.8rem',
                lineHeight: 1.2,
              }}
            >
              Complete todos los campos requeridos
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', mt: 0.25, fontSize: '0.7rem' }}
            >
              Monto, días, tipo de garantía y tasa de conversión
            </Typography>
          </Box>
        )}
      </Box>
      <Alert
        severity="info"
        icon={<InfoIcon sx={{ fontSize: '0.9rem' }} />}
        sx={{
          borderRadius: 0.5,
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          border: '1px solid rgba(25, 118, 210, 0.2)',
          py: 0.75,
          '& .MuiAlert-message': { py: 0.25 },
        }}
      >
        <AlertTitle sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
          Información Importante
        </AlertTitle>
        <Typography
          variant="body2"
          sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
        >
          {warrantyRates
            ? 'Este cálculo utiliza las tasas específicas de la garantía.'
            : 'Este es un valor referencial. El monto total a pagar puede variar al momento de hacer efectiva la solicitud de la garantía.'}
        </Typography>
      </Alert>
    </Paper>
  );
};

export default RiskPremiumCalculator;
